import {
  AlertOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  ScheduleOutlined,
  SettingOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Tooltip as AntDToolTip,
  Button,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  camelToWords,
  formatCurrency,
  formatDate,
  getCustomFieldsItems,
  idToObjectMap,
  insertIf,
  snakeToCamelCase,
  toTitleCase,
} from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { TriggerModal } from "components/triggerModal";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import currency from "currency.js";
import {
  useAssignAccountsMutation,
  useFetchDebtorProfileQuery,
} from "features/accountsTable/agencyPortal/accountsTableAPI";
import { TableAppearanceModal } from "features/appearance";
import { reconcileColumns } from "features/appearance/components/tableAppearanceModal";
import { TABLE_COLUMNS } from "features/appearance/constants";
import { useUserType } from "features/auth";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import { useFetchCreditorsQuery } from "features/creditors/agencyPortal/creditorsAPI";
import {
  useAssignDesksToAccountMutation,
  useBulkUpdateFollowUpDateMutation,
  useBulkUpdateStatusCodeMutation,
  useDeleteAccountMutation,
  useFetchDebtorAccountsQuery,
  useFetchHiddenDesksQuery,
  usePostAssignAccountsToForwardingEntityMutation,
  useUpdateAccountStatusMutation,
  useUpdateFollowUpDateMutation,
} from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import AccountSequencesModal from "features/debtorAccountsTable/components/accountSequencesModal";
import BalanceHistoryModal from "features/debtorAccountsTable/components/balanceHistoryModal";
import CreditBureauSettingsModal from "features/debtorAccountsTable/components/creditBureauSettingsModal";
import CreditReportHistoryModal from "features/debtorAccountsTable/components/creditReportHistoryModal";
import ManageCodebtorsModal from "features/debtorAccountsTable/components/manageCodebtorsModal";
import ManageDisputeFromAccountModal from "features/debtorAccountsTable/components/manageDisputeFromAccountModal";
import AccountDisputesTable from "features/debtorAccountsTable/components/accountDisputesTable";
import UpdateAccountModal from "features/debtorProfile/components/updateAccountModal";
import { useFetchForwardingEntitiesQuery } from "features/forwardingEntities/forwardingEntitiesAPI";
import {
  activeCollectorsWithRolesSelector,
  useFetchAgencyWorkflowStates,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { collectorFullName } from "features/home/utils";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useFetchAllAccountUdfCustomFieldsQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import { useAppearance } from "providers/appearanceProvider";
import { useCompactMode } from "providers/compactModeProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { UserDefinedFieldValues } from "components/userDefinedFieldValues";
import moment from "moment-timezone";
import { useFetchDesksQuery } from "features/desksTable/desksAPI";
import { DATE_TIME_FORMAT } from "common/constants";
import AccountRowExpansion from "features/debtorAccountsTable/agencyPortal/components/accountRowExpansion";

const StyledTable = styled(Table)`
  cursor: pointer;
  width: 100%;
  & .ant-table-cell:not(th) {
    padding: 2px 8px !important;
  }
`;
const StyledTag = styled(Tag)`
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const StyledInfoTitle = styled.div`
  color: grey;
`;

const StyledInfoValue = styled.div`
  margin-left: 4px;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledButton = styled(Button)`
  padding-left: 0;
`;

const ButtonWithMargin = styled(Button)`
  margin: 0px 10px;
  &:hover {
    background: #f0f0f0;
  }
`;

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  font-size: 24px;
`;

const StyledSecondaryLine = styled.div`
  font-size: 11px;
  color: #888;
  line-height: 1.4;
  margin-top: 1px;
`;

const StyledDeskSelect = styled(Select)`
  min-width: 120px;
  .ant-select-selector {
    border-bottom: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
  }
`;

const StyledStatusSelect = styled(Select)`
  min-width: 160px;
  .ant-select-selector {
    border-bottom: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
  }
`;

const StyledCollectorSelect = styled(Select)`
  min-width: 140px;
  .ant-select-selector {
    border-bottom: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
  }
`;

const StyledRolledUpHeading = styled.h4`
  margin: 8px 0 4px 16px;
`;

const StyledActionContainer = styled(Row)`
  background: #fff;
  height: 48px;
  margin-top: 1px;
  border-left: 1px solid #f0f0f0;
  align-items: center;
  flex: 1;
  min-width: 200px;
  display: flex;
  justify-content: space-between;
`;

const StyledActions = styled(Row)`
  border-top: 1px solid #f0f0f0;
  border-right: 1px solid #f0f0f0;
  height: 100%;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledActionItems = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSwitchLabel = styled.span`
  margin-left: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`;

const TABLE_ID = "debtorAccounts";

function DebtorAccountsTable({ debtorId }) {
  const { isCreditorUserType, isAgencyUserType } = useUserType();
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const hasAppliedAppearance = useRef(false);
  const [activeRecord, setActiveRecord] = useState(undefined);
  const [isDisputeModalVisible, setDisputeModalVisible] = useState(false);
  const [isDisputesTableVisible, setDisputesTableVisible] = useState(false);
  const [isBalanceHistoryModalVisible, setBalanceHistoryModalVisible] = useState(false);
  const [isCreditBureauSettingsModalVisible, setCreditBureauSettingsModalVisible] = useState(false);
  const [isManageCodebtorsModalVisible, setManageCodebtorsModalVisible] = useState(false);
  const [isUpdateAccountModalVisible, setUpdateAccountModalVisible] = useState(false);
  const [isSequencesModalVisible, setSequencesModalVisible] = useState(false);
  const [isCreditReportHistoryModalVisible, setCreditReportHistoryModalVisible] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkAssignCollectorModalVisible, setBulkAssignCollectorModalVisible] = useState(false);
  const [isBulkUpdateFollowUpDateModalVisible, setBulkUpdateFollowUpDateModalVisible] =
    useState(false);
  const [isBulkUpdateStatusCodeModalVisible, setBulkUpdateStatusCodeModalVisible] = useState(false);
  const [bulkAssignCollectorForm] = Form.useForm();
  const [bulkUpdateFollowUpDateForm] = Form.useForm();
  const [bulkUpdateStatusCodeForm] = Form.useForm();
  const navigate = useNavigate();
  const { isCompactMode } = useCompactMode();
  const [assignAccountsToAgent, { isLoading: isAssigningCollector }] = useAssignAccountsMutation();
  const [updateFollowUpDate] = useUpdateFollowUpDateMutation();
  const [deleteAccount] = useDeleteAccountMutation();
  const [assignDesksToAccount] = useAssignDesksToAccountMutation();
  const [assignAccountsToForwardingEntity] = usePostAssignAccountsToForwardingEntityMutation();
  const [updateAccountStatus] = useUpdateAccountStatusMutation();
  const { data: debtorAccounts, isLoading, isFetching } = useFetchDebtorAccountsQuery({ debtorId });
  const { data: debtorPersonalInfo } = useFetchDebtorProfileQuery({ debtorId });
  const { data: agencySettings } = useFetchAgencyQuery();
  const { data: hiddenDesks } = useFetchHiddenDesksQuery({ id: debtorId });
  const [bulkUpdateFollowUpDate] = useBulkUpdateFollowUpDateMutation();
  const [bulkUpdateStatusCode] = useBulkUpdateStatusCodeMutation();
  const { activeCollectorsWithRoles: collectors, isLoading: isCollectorsLoading } =
    useFetchCollectorsQuery(undefined, {
      selectFromResult: (result) => ({
        ...result,
        activeCollectorsWithRoles: activeCollectorsWithRolesSelector(result),
      }),
    });

  const { data: forwardingEntities } = useFetchForwardingEntitiesQuery();
  const { data: creditors } = useFetchCreditorsQuery(
    { filters: { creditorIds: debtorAccounts?.map((account) => account.creditorId) } },
    { skip: !debtorAccounts || debtorAccounts?.length === 0 },
  );
  const { data: defaultWorkflowStates } = useFetchAgencyWorkflowStates();
  const {
    data: {
      [PERMISSIONS.ACCOUNT__DELETE]: isAccountDeleteAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_FOLLOW_UP_DATE]: isChangeFollowUpDateAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_FORWARDING_ENTITY]: isChangeForwardingAgencyAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_COLLECTOR]: isChangeCollectorAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_GENERAL_INFO]: isAccountEditAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_STATUS_CODE]: isChangeStatusCodeAuthorized,
      [PERMISSIONS.ACCOUNT_BALANCE_HISTORY__GET]: isViewBalanceHistory,
      [PERMISSIONS.ACCOUNT_CBR_CONFIG__GET]: isViewCreditBureauAuthorized,
      [PERMISSIONS.ACCOUNT_CODEBTORS__GET]: isViewCodebtorsAuthorized,
      [PERMISSIONS.ACCOUNT_COMMUNICATION_SEQUENCE__GET]: isAccountCommunicationSequenceAuthorized,
      [PERMISSIONS.ACCOUNT_CREDIT_REPORT_HISTORY__GET]: isViewCreditReportHistoryAuthorized,
      [PERMISSIONS.DISPUTE__GET]: isDisputeGetAuthorized,
      [PERMISSIONS.APPEARANCE__UPDATE]: isAppearanceUpdateAuthorized,
      [PERMISSIONS.ACCOUNT__UPDATE_DESK]: isUpdateDeskAuthorized,
    },
  } = useAuthorizations();

  const { data: accountCustomFields } = useFetchAllAccountUdfCustomFieldsQuery();
  const { appearance, isLoading: isAppearanceLoading } = useAppearance();

  useEffect(() => {
    if (!isAppearanceLoading && !hasAppliedAppearance.current && appearance !== undefined) {
      hasAppliedAppearance.current = true;
      const saved = appearance?.items?.[TABLE_ID]?.showActiveOnly;
      if (saved !== undefined) {
        setShowActiveOnly(saved);
      }
    }
  }, [isAppearanceLoading, appearance]);

  const { data: desks = [] } = useFetchDesksQuery();
  const deskIdToDeskMap = useMemo(() => {
    if (!desks) {
      return {};
    }
    return idToObjectMap(desks);
  }, [desks]);

  const creditorIdToCreditorMap = useMemo(() => {
    if (!creditors) {
      return {};
    }
    return idToObjectMap(creditors.results);
  }, [creditors]);

  const visibleAccounts = useMemo(
    () =>
      debtorAccounts?.filter(
        (account) => !account.legalParentId && (!showActiveOnly || !account.isClosed),
      ) ?? [],
    [debtorAccounts, showActiveOnly],
  );

  const activeStatusFilters = useMemo(() => {
    if (!visibleAccounts.length || !defaultWorkflowStates) return undefined;
    const activeCodes = new Set(visibleAccounts.map((a) => a.statusCode).filter(Boolean));
    return defaultWorkflowStates
      .filter((state) => activeCodes.has(state.code))
      .map((state) => ({
        value: state.code,
        text: [state.code, state.name].join(" - "),
      }));
  }, [visibleAccounts, defaultWorkflowStates]);

  const isChangeFollowUpDateDisabled = (record) => {
    return record.status === "judgment" || isCreditorUserType || !isChangeFollowUpDateAuthorized;
  };
  const isChangeStatusCodeDisabled = (record) => {
    return record.status === "judgment" || isCreditorUserType || !isChangeStatusCodeAuthorized;
  };
  const isChangeTurnoverDateDisabled = (record) => {
    return record.status === "judgment" || isCreditorUserType;
  };
  const isChangeForwardingAgencyDisabled = (record) => {
    return (
      record.status === "judgment" || isCreditorUserType || !isChangeForwardingAgencyAuthorized
    );
  };
  const isChangeCollectorDisabled = (record) => {
    return record.status === "judgment" || isCreditorUserType || !isChangeCollectorAuthorized;
  };

  const selectedAccountIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);
  const onSelectedAccountChange = (_, selectedRowsData) => {
    setSelectedRows(selectedRowsData);
  };
  const rowSelection = {
    selectedRowKeys: selectedRows.map((row) => row.id),
    onChange: onSelectedAccountChange,
    preserveSelectedRowKeys: true,
  };
  const hasSelected = selectedRows.length > 0;
  const canBulkAssignCollector =
    hasSelected && selectedRows.every((row) => !isChangeCollectorDisabled(row));
  const canBulkUpdateFollowUpDate =
    hasSelected && selectedRows.every((row) => !isChangeFollowUpDateDisabled(row));
  const canBulkUpdateStatusCode =
    hasSelected && selectedRows.every((row) => !isChangeStatusCodeDisabled(row));

  const bulkAssignTooltip = (action, canAssign) => {
    if (!hasSelected) {
      return `Select account(s) to ${action}`;
    }
    if (!canAssign) {
      return `Selection includes accounts that cannot be ${action}`;
    }
    const label = selectedRows.length === 1 ? "Account" : "Accounts";
    return `${toTitleCase({ str: action, delimiter: "_" })} for ${selectedRows.length} ${label}`;
  };

  const closeBulkAssignCollectorModal = () => {
    bulkAssignCollectorForm.resetFields();
    setBulkAssignCollectorModalVisible(false);
  };

  const closeBulkUpdateFollowUpDateModal = () => {
    bulkUpdateFollowUpDateForm.resetFields();
    setBulkUpdateFollowUpDateModalVisible(false);
  };

  const closeBulkUpdateStatusCodeModal = () => {
    bulkUpdateStatusCodeForm.resetFields();
    setBulkUpdateStatusCodeModalVisible(false);
  };

  const handleBulkAssignCollector = async () => {
    const values = await bulkAssignCollectorForm.validateFields();
    const result = await assignAccountsToAgent({
      accountIds: selectedAccountIds,
      agentId: values.assignToAgentId ?? null,
    });
    if ("data" in result) {
      const assignee = collectors?.find((collector) => collector.id === values.assignToAgentId);
      const assigneeLabel = assignee ? collectorFullName(assignee) : "Unassigned";
      message.success(
        `${selectedAccountIds.length} ${
          selectedAccountIds.length === 1 ? "account" : "accounts"
        } assigned to ${assigneeLabel}`,
      );
      setSelectedRows([]);
      closeBulkAssignCollectorModal();
      return;
    }
    if ("error" in result) {
      bulkAssignCollectorForm.setFields(formatError(result.error));
      message.error("Failed to assign accounts. Please try again later.");
    }
  };

  const handleBulkUpdateFollowUpDate = async () => {
    const values = await bulkUpdateFollowUpDateForm.validateFields();
    const results = await bulkUpdateFollowUpDate({
      followUpDate: values.followUpDate,
      accountIds: selectedRows.map((row) => row.id),
    });
    if ("data" in results) {
      message.success(
        `Follow-up date updated for ${selectedAccountIds.length} ${
          selectedAccountIds.length === 1 ? "account" : "accounts"
        }`,
      );
      setSelectedRows([]);
      closeBulkUpdateFollowUpDateModal();
    }
    if ("error" in results) {
      bulkUpdateFollowUpDateForm.setFields(formatError(results.error));
    }
  };

  const handleBulkUpdateStatusCode = async () => {
    const values = await bulkUpdateStatusCodeForm.validateFields();
    const newStatus = defaultWorkflowStates?.find((state) => state.id === values.statusId);
    if (!newStatus) {
      message.error("Unable to update status code. Please try again later.");
      return;
    }
    const results = await bulkUpdateStatusCode({
      workflowStateId: newStatus.id,
      accountIds: selectedRows.map((row) => row.id),
    });
    if ("data" in results) {
      message.success(
        `Status code updated for ${selectedAccountIds.length} ${
          selectedAccountIds.length === 1 ? "account" : "accounts"
        }`,
      );
    }
    if ("error" in results) {
      bulkUpdateStatusCodeForm.setFields(formatError(results.error));
    }

    setSelectedRows([]);
    closeBulkUpdateStatusCodeModal();
  };

  const onDoneCreditBureauSettingsModal = async () => {
    setCreditBureauSettingsModalVisible(false);
    setActiveRecord(null);
  };

  const onUpdateAccountStatus = async (record, newStatus) => {
    const result = await updateAccountStatus({
      workflowTaskId: record.workflowTaskId,
      stateId: newStatus.id,
    });
    if ("data" in result) {
      message.success("Account status changed successfully!");
    }
  };

  const onChangeFollowUpDate = async (accountId, date) => {
    const result = await updateFollowUpDate({ followUpDate: date, accountId });
    if ("data" in result) {
      message.success("Follow-up date changed successfully!");
    }
  };

  const onChangeAssignAgent = async ({ selectedAccountId, agentId }) => {
    const result = await assignAccountsToAgent({
      accountIds: [selectedAccountId],
      agentId,
    });
    if ("data" in result) {
      const assignee = collectors?.find((collector) => collector.id === agentId);
      if (!assignee) {
        message.success("Collector was unassigned successfully!");
      } else {
        message.success(`${collectorFullName(assignee)} was assigned successfully!`);
      }
    }
  };

  const onChangeForwardingEntity = async ({ selectedAccountId, forwardingEntityId }) => {
    const result = await assignAccountsToForwardingEntity({
      accountIds: [selectedAccountId],
      forwardingEntityId,
    });
    if ("data" in result) {
      const assignedForwardingEntity = forwardingEntities?.find(
        (forwardingEntity) => forwardingEntity.id === forwardingEntityId,
      );
      if (!assignedForwardingEntity) {
        message.success("Forwarding entity was unassigned successfully!");
      } else {
        message.success(`${assignedForwardingEntity.name} was assigned successfully!`);
      }
    }
  };

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onDoneSequencesModal = () => {
    setSequencesModalVisible(false);
    setActiveRecord(null);
  };

  const onCreditBureauSettingsModalCancel = () => {
    setCreditBureauSettingsModalVisible(false);
    setActiveRecord(null);
  };

  const onManageCodebtorsModalCancel = () => {
    setManageCodebtorsModalVisible(false);
    setActiveRecord(null);
  };

  const onDoneUpdateAccountModal = () => {
    setUpdateAccountModalVisible(false);
    setActiveRecord(null);
  };

  const onUpdateAccountModalCancel = () => {
    setUpdateAccountModalVisible(false);
    setActiveRecord(null);
  };

  const onSequencesModalCancel = () => {
    setSequencesModalVisible(false);
    setActiveRecord(null);
  };

  const onBalanceHistoryModalCancel = () => {
    setBalanceHistoryModalVisible(false);
    setActiveRecord(null);
  };

  const onDisputesTableCancel = () => {
    setDisputesTableVisible(false);
    setActiveRecord(null);
  };

  const onManageDisputeModalCancel = () => {
    setDisputeModalVisible(false);
    setActiveRecord(null);
  };

  const onCreditReportHistoryModalCancel = () => {
    setCreditReportHistoryModalVisible(false);
    setActiveRecord(null);
  };

  const dropdownItems = (record = null) =>
    [
      {
        label: "Configure Credit Bureau Settings",
        key: "bureau",
        hidden: !isViewCreditBureauAuthorized,
      },
      {
        label: "Manage Codebtors",
        key: "codebtors",
        hidden: !isViewCodebtorsAuthorized,
      },
      {
        label: "Manage Communication Sequence",
        key: "sequences",
        hidden: !isAccountCommunicationSequenceAuthorized,
      },
      {
        label: "View Balance History",
        key: "balances",
        hidden: !isViewBalanceHistory,
      },
      {
        label: "Manage Disputes",
        key: "disputes",
        hidden: !isDisputeGetAuthorized,
      },
      {
        label: "View Credit Report History",
        key: "creditReport",
        hidden: !isViewCreditReportHistoryAuthorized,
      },
    ]
      .filter((item) => !item.hidden)
      // insert dividers between items
      .flatMap((value, index, array) =>
        index < array.length - 1
          ? [
              value,
              {
                type: "divider",
              },
            ]
          : value,
      );

  const handleDelete = async (record) => {
    const result = await deleteAccount({ accountId: record.id });
    if ("data" in result) {
      message.success("Account removed successfully!");
    }
    if ("error" in result) {
      message.error("Unable to remove account");
    }
  };

  const configureCreditBureauSettings = (record) => {
    setCreditBureauSettingsModalVisible(true);
    setActiveRecord(record);
  };

  const configureCodebtors = (record) => {
    setManageCodebtorsModalVisible(true);
    setActiveRecord(record);
  };

  const configureSequences = (record) => {
    setSequencesModalVisible(true);
    setActiveRecord(record);
  };

  const viewBalanceHistory = (record) => {
    setBalanceHistoryModalVisible(true);
    setActiveRecord(record);
  };

  const viewCreditReportHistory = (record) => {
    setCreditReportHistoryModalVisible(true);
    setActiveRecord(record);
  };

  const manageDisputes = (record) => {
    setDisputesTableVisible(true);
    setActiveRecord(record);
  };

  const manageDispute = (record) => {
    setDisputeModalVisible(true);
    setActiveRecord(record);
  };

  const handleMenuClick = ({ key, domEvent }, record) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    // Call the action handler defined in the items dictionary
    const action = {
      disputes: manageDisputes,
      bureau: configureCreditBureauSettings,
      codebtors: configureCodebtors,
      sequences: configureSequences,
      balances: viewBalanceHistory,
      dispute: manageDispute,
      creditReport: viewCreditReportHistory,
    }[key];
    action(record);
  };

  const onUpdateAccountClick = (record) => {
    setUpdateAccountModalVisible(true);
    setActiveRecord(record);
  };

  const onUpdateAccountDesks = async (record, newDeskId) => {
    const result = await assignDesksToAccount({ id: record.id, deskId: newDeskId });
    if ("data" in result) {
      message.success("Desk updated successfully!");
    }
  };

  const compactColumns = [
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__IDENTIFIERS,
      title: "Identifiers",
      fixed: "left",
      width: 220,
      render: (text, record) => (
        <div>
          <div>{record?.externalId ?? "-"}</div>
          <StyledSecondaryLine>
            Debtor: {record?.debtorExternalId ?? "-"} · Ref: {record?.clientReferenceId ?? "-"}
          </StyledSecondaryLine>
        </div>
      ),
      filters: useMemo(() => {
        if (debtorAccounts) {
          const externalIdOptions = [
            ...new Set(
              debtorAccounts.map((debtorAccount) => debtorAccount.externalId).filter(Boolean),
            ),
          ].map((id) => ({ text: id, value: id }));
          const clientRefIdOptions = [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.clientReferenceId)
                .filter(Boolean),
            ),
          ].map((id) => ({ text: id, value: id }));
          return [
            { text: "External ID", value: "external_id", children: externalIdOptions },
            { text: "Client Ref ID", value: "client_reference_id", children: clientRefIdOptions },
          ];
        }
      }, [debtorAccounts]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) => {
        if (value === "external_id" || value === "client_reference_id") return true;
        return (
          record.externalId?.toString().toLowerCase().includes(value.toString().toLowerCase()) ||
          record.clientReferenceId
            ?.toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase())
        );
      },
      hidden: isCreditorUserType,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__CURRENT_BALANCE,
      title: "Balance",
      width: 175,
      render: (_, record) => {
        const makeBalancePopover = (items) => (
          <Col>
            <Descriptions bordered column={1} size="small">
              {items.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );

        const currentItems = [
          { key: "principal", label: "Principal", value: formatCurrency(record.principalBalance) },
          {
            key: "interest",
            label: `Agency Interest (${
              currency(record?.interestRate, { precision: 4 }).multiply(100).value
            }%)`,
            value: formatCurrency(record.interestBalance),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(record.clientInterestBalance),
          },
          ...insertIf(record.legalStatus === "judgment", {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(record.prejudgmentInterestBalance),
          }),
          { key: "totalFees", label: "Fees", value: formatCurrency(record.totalFeesBalance) },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.totalBalance)}</strong>,
          },
        ];

        const originalItems = [
          {
            key: "originalPrincipal",
            label: "Principal",
            value: formatCurrency(record.originalPrincipal),
          },
          {
            key: "originalInterest",
            label: `Agency Interest (${
              currency(record?.interestRate, { precision: 4 }).multiply(100).value
            }%)`,
            value: formatCurrency(record.originalInterest),
          },
          {
            key: "originalClientInterest",
            label: "Client Interest",
            value: formatCurrency(record.originalClientInterest),
          },
          ...insertIf(record.legalStatus === "judgment", {
            key: "originalPrejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(record.originalPrejudgmentInterest),
          }),
          {
            key: "originalTotalFeesDue",
            label: "Fees",
            value: formatCurrency(record.originalTotalFeesDue),
          },
          {
            key: "originalTotal",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.originalTotal)}</strong>,
          },
        ];

        return (
          <div>
            <Row align="middle">
              <StyledInfoTitle>Orig:&nbsp;</StyledInfoTitle>
              <StyledInfoValue>{formatCurrency(record.originalTotal)}</StyledInfoValue>
              <Popover
                content={makeBalancePopover(originalItems)}
                title="Original Balance Breakdown"
                trigger="hover"
                placement="right"
              >
                <StyledQuestionCircleOutlined />
              </Popover>
            </Row>
            <Row align="middle">
              <Tooltip title="Turnover Balance">
                <StyledInfoTitle>TO:&nbsp;</StyledInfoTitle>
              </Tooltip>
              <StyledInfoValue>{formatCurrency(record.turnoverTotalBalance)}</StyledInfoValue>
            </Row>
            <Row align="middle">
              <StyledInfoTitle>Cur:&nbsp;</StyledInfoTitle>
              <StyledInfoValue>{formatCurrency(record.totalBalance)}</StyledInfoValue>
              <Popover
                content={makeBalancePopover(currentItems)}
                title="Current Balance Breakdown"
                trigger="hover"
                placement="right"
              >
                <StyledQuestionCircleOutlined />
              </Popover>
            </Row>
          </div>
        );
      },
      sorter: { compare: (a, b) => a.totalBalance - b.totalBalance },
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__ORIGINAL_CLIENT,
      title: "Creditor",
      width: 200,
      render: (text, record) => {
        const creditor = creditorIdToCreditorMap[record.creditorId];
        const popoverContent = (
          <>
            <Row wrap={false}>
              <StyledInfoTitle>Current Creditor:</StyledInfoTitle>
              <StyledInfoValue>{record.currentCreditorName || "-"}</StyledInfoValue>
            </Row>
            <Row wrap={false}>
              <StyledInfoTitle>Original Creditor:</StyledInfoTitle>
              <StyledInfoValue>{record.originalCreditorName || "-"}</StyledInfoValue>
            </Row>
            {creditor && (
              <>
                <Row align="middle">
                  <StyledInfoTitle>Code:</StyledInfoTitle>
                  <StyledInfoValue>{creditor.code || "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Primary Contact:</StyledInfoTitle>
                  <StyledInfoValue>{creditor.bestContact?.name || "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Email:</StyledInfoTitle>
                  <StyledInfoValue>{creditor.bestContact?.email || "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Phone:</StyledInfoTitle>
                  <StyledInfoValue>{creditor.bestContact?.workPhone || "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Address:</StyledInfoTitle>
                  <StyledInfoValue>{creditor.bestAddress?.formatted || "-"}</StyledInfoValue>
                </Row>
              </>
            )}
          </>
        );
        return (
          <div>
            <Row wrap={false}>
              <StyledInfoTitle>Client:</StyledInfoTitle>
              <StyledInfoValue>
                {creditor ? (
                  <>
                    <a href={`/creditors/${creditor.id}`} target="_blank" rel="noreferrer">
                      {creditor.name}
                    </a>
                    <Popover content={popoverContent} title="Creditor Details" trigger="click">
                      <StyledInfoCircleOutlined />
                    </Popover>
                  </>
                ) : (
                  "-"
                )}
              </StyledInfoValue>
            </Row>
            <Row wrap={false}>
              <StyledInfoTitle>Orig:</StyledInfoTitle>
              <StyledInfoValue>{record.originalCreditorName || "-"}</StyledInfoValue>
            </Row>
          </div>
        );
      },
      hideForRolledUpAccounts: true,
      hidden: isCreditorUserType,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__STATUS,
      title: "Status",
      dataIndex: "statusName",
      render: (text, record) => {
        return (
          <StyledStatusSelect
            showSearch
            variant="borderless"
            filterOption={filterOption}
            disabled={isChangeStatusCodeDisabled(record)}
            value={!text ? "" : `${record.statusCode} - ${text}`}
            popupMatchSelectWidth={400}
            onChange={async (value) => {
              const newStatus = defaultWorkflowStates?.find((state) => state.id === value);
              if (text?.toLowerCase() !== newStatus?.name?.toLowerCase()) {
                await onUpdateAccountStatus(record, newStatus);
              }
            }}
            options={defaultWorkflowStates?.map((state) => ({
              value: state.id,
              label: [state.code, state.name].join(" - "),
            }))}
          />
        );
      },
      filters: activeStatusFilters,
      filterMode: "menu",
      filterSearch: true,
      onFilter: (value, record) =>
        record.statusCode?.toString().toLowerCase().includes(value.toLowerCase()),
      hideForRolledUpAccounts: true,
      visible: true,
      sorter: {
        compare: (a, b) => {
          if (a.statusName < b.statusName) {
            return -1;
          }
          if (a.statusName > b.statusName) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__PAY_READY_FIELDS,
      title: "Pay Ready Fields",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Current Status:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.currentAccountStatuses?.join(", ") ?? "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Status Last Updated:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.accountStatusUpdatedAt
                ? moment(record?.payReadyLease?.accountStatusUpdatedAt).format(DATE_TIME_FORMAT)
                : "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Last Synced:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.lastSyncedAt
                ? moment(record?.payReadyLease?.lastSyncedAt).format(DATE_TIME_FORMAT)
                : "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Move In Date:</StyledInfoTitle>
            <StyledInfoValue>
              {formatDate(record?.payReadyLease?.moveInDate) ?? "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Move Out Date:</StyledInfoTitle>
            <StyledInfoValue>
              {formatDate(record?.payReadyLease?.moveOutDate) ?? "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Lease Begin Date:</StyledInfoTitle>
            <StyledInfoValue>
              {formatDate(record?.payReadyLease?.leaseBeginDate) ?? "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Lease End Date:</StyledInfoTitle>
            <StyledInfoValue>
              {formatDate(record?.payReadyLease?.leaseEndDate) ?? "-"}
            </StyledInfoValue>
          </Row>
          <Popover
            content={
              <>
                <Row align="middle">
                  <StyledInfoTitle>Current Status:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.currentAccountStatuses?.join(", ") || "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Status Last Updated:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.accountStatusUpdatedAt
                      ? moment(record?.payReadyLease?.accountStatusUpdatedAt).format(
                          DATE_TIME_FORMAT,
                        )
                      : "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Last Synced:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.lastSyncedAt
                      ? moment(record?.payReadyLease?.lastSyncedAt).format(DATE_TIME_FORMAT)
                      : "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Synced Account Statuses:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.syncedAccountStatuses?.join(", ") ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Placement Status:</StyledInfoTitle>
                  <StyledInfoValue>
                    {camelToWords(snakeToCamelCase(record?.payReadyLease?.placementStatus)) || "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Move In Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.payReadyLease?.moveInDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Move Out Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.payReadyLease?.moveOutDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Lease Begin Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.payReadyLease?.leaseBeginDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Lease End Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.payReadyLease?.leaseEndDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Property ID:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.propertyId ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Management Company ID:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.managementCompanyId ?? "-"}
                  </StyledInfoValue>
                </Row>
              </>
            }
            title="PayReady Fields"
            trigger="click"
          >
            <StyledButton type="link">See All</StyledButton>
          </Popover>
        </Col>
      ),
      visible: agencySettings?.isPayReadyEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__INTELITECH_SCORES,
      title: "Intelitech Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>I-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.iScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Medal-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.medalScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Q-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.qScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>R-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.rScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>FPG-Value:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.fpgValue ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isIntelitechEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__LEXIS_NEXIS_SCORES,
      title: "Lexis Nexis Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.lexisNexisScore?.customScore ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isLexisNexisEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__TRANS_UNION_SCORES,
      title: "Trans Union Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>DEG Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.transUnionScore?.degScore ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isTransUnionEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__COLLECTOR,
      title: "Collector",
      dataIndex: "assigneeId",
      render: (text, record) => (
        <StyledCollectorSelect
          showSearch
          variant="borderless"
          filterOption={filterOption}
          loading={isCollectorsLoading}
          disabled={isChangeCollectorDisabled(record)} // TODO: update when new status is defined on BE. All accounts that are 'rolled up' will not be editable
          popupMatchSelectWidth={false}
          value={text ?? ""}
          onChange={async (value) => {
            if (value !== text) {
              await onChangeAssignAgent({
                selectedAccountId: record.id,
                agentId: value === -1 ? null : value,
              });
            }
          }}
          options={[
            {
              label: "Unassigned",
              value: -1,
            },
            ...(collectors.map((collector) => ({
              label: collectorFullName(collector),
              value: collector.id,
            })) ?? []),
          ]}
          placeholder="Select one..."
        />
      ),
      hideForRolledUpAccounts: true,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__DESK,
      title: "Desk",
      dataIndex: "deskId",
      render: (text, record) => (
        <StyledDeskSelect
          showSearch
          variant="borderless"
          filterOption={filterOption}
          popupMatchSelectWidth={false}
          value={text ?? ""}
          disabled={!isUpdateDeskAuthorized}
          onChange={async (value) => {
            await onUpdateAccountDesks(record, value === -1 ? null : value);
          }}
          options={[
            {
              label: "Unassigned",
              value: -1,
            },
            ...(desks?.map((desk) => ({
              label: desk.name,
              value: desk.id,
            })) ?? []),
          ]}
          placeholder="Select one..."
        />
      ),
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__FOLLOW_UP_DATE,
      title: "Follow-up Date",
      dataIndex: "followUpDate",
      render: (text, record) => (
        <AktDatePicker
          disabled={isChangeFollowUpDateDisabled(record)}
          value={text}
          onChange={(date) => {
            if (date !== text) {
              onChangeFollowUpDate(record.id, date);
            }
          }}
        />
      ),
      hideForRolledUpAccounts: true,
      visible: true,
      sorter: {
        compare: (a, b) => {
          if (a.followUpDate < b.followUpDate) {
            return -1;
          }
          if (a.followUpDate > b.followUpDate) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__LAWSUIT_CASE_NUMBER,
      title: "Legal Case #",
      dataIndex: "lawsuitCaseNumber",
      render: (text, record) => (
        <div>
          <div>{record.lawsuitCaseNumber ?? "-"}</div>
          {record.judgmentDate && (
            <StyledSecondaryLine>Judgment: {formatDate(record.judgmentDate)}</StyledSecondaryLine>
          )}
        </div>
      ),
      visible: true,
      filters: useMemo(() => {
        if (debtorAccounts) {
          return [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.lawsuitCaseNumber)
                .filter((lawsuitCaseNumber) => lawsuitCaseNumber),
            ),
          ].map((lawsuitCaseNumber) => ({
            text: lawsuitCaseNumber,
            value: lawsuitCaseNumber,
          }));
        }
      }, [debtorAccounts]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) =>
        record.lawsuitCaseNumber?.toString().toLowerCase().includes(value.toString().toLowerCase()),
      sorter: (a, b) => a?.lawsuitCaseNumber?.localeCompare(b?.lawsuitCaseNumber),
      sortDirections: ["descend", "ascend"],
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__ACTIONS,
      title: "Actions",
      fixed: "right",
      align: "center",
      dataIndex: "action",
      render: (_, record) => (
        <Row wrap={false} align="middle">
          {/* <AntDToolTip placement="bottom" title="View Payment History">
              <HistoryOutlined
                key="history"
                onClick={() =>
                  setModalContent(
                    <MakeAPaymentModal
                      debts={debtorProfileSlice.accounts}
                      title="Debt"
                      open
                      onOk={paymentOnOk}
                      onCancel={onCancel}
                    />,
                  )
                }
              />
            </AntDToolTip> */}
          {isAgencyUserType && (
            <Space size="middle">
              {isAccountEditAuthorized && (
                <AntDToolTip placement="bottom" title="Edit" key="edit">
                  <EditOutlined key="edit" onClick={() => onUpdateAccountClick(record)} />
                </AntDToolTip>
              )}
              {isAccountDeleteAuthorized && (
                <Popconfirm
                  placement="topLeft"
                  okText="Yes"
                  title="Are you sure you want to delete this account?"
                  onConfirm={() => handleDelete(record)}
                >
                  <DeleteOutlined key="delete-account" />
                </Popconfirm>
              )}
            </Space>
          )}
          {dropdownItems(record).length > 0 && (
            <Dropdown
              menu={{
                // @ts-ignore
                items: dropdownItems(record),
                onClick: (clickHandlerProp) => handleMenuClick(clickHandlerProp, record),
              }}
              trigger={["click"]}
            >
              <Button type="text">
                <Space>
                  <StyledEllipsisOutlined />
                </Space>
              </Button>
            </Dropdown>
          )}
        </Row>
      ),
      hideForRolledUpAccounts: true,
      hidden:
        dropdownItems().length === 0 &&
        (!isAgencyUserType || (!isAccountEditAuthorized && !isAccountDeleteAuthorized)),
      visible: true,
    },
  ].filter((column) => !column.hidden);

  const classicColumns = [
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__IDENTIFIERS,
      title: "Identifiers",
      fixed: "left",
      width: 220,
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Account ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.id ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Debtor ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.debtorExternalId ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>External ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.externalId ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Client Ref ID:</StyledInfoTitle>
            <StyledInfoValue>{record?.clientReferenceId ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      filters: useMemo(() => {
        if (debtorAccounts) {
          const externalIdOptions = [
            ...new Set(
              debtorAccounts.map((debtorAccount) => debtorAccount.externalId).filter(Boolean),
            ),
          ].map((id) => ({ text: id, value: id }));
          const clientRefIdOptions = [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.clientReferenceId)
                .filter(Boolean),
            ),
          ].map((id) => ({ text: id, value: id }));
          return [
            { text: "External ID", value: "external_id", children: externalIdOptions },
            { text: "Client Ref ID", value: "client_reference_id", children: clientRefIdOptions },
          ];
        }
      }, [debtorAccounts]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) => {
        if (value === "external_id" || value === "client_reference_id") return true;
        return (
          record.externalId?.toString().toLowerCase().includes(value.toString().toLowerCase()) ||
          record.clientReferenceId
            ?.toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase())
        );
      },
      hidden: isCreditorUserType,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__TAGS,
      title: "Tags",
      fixed: "left",
      width: 0,
      render: (text, record) => (
        <Col>
          {record?.isClosed ? <StyledTag color="red">closed</StyledTag> : null}
          {record?.forwardingEntityId !== null ? (
            <StyledTag color="blue">forwarded</StyledTag>
          ) : null}
          {record?.hasNonResolvedDisputes ? <StyledTag color="red">disputed</StyledTag> : null}
          {record?.hasAnyResolvedDisputes ? (
            <StyledTag color="green">dispute resolved</StyledTag>
          ) : null}
          {record?.statuteOfLimitationsExpiryDate &&
          moment().isAfter(moment(record.statuteOfLimitationsExpiryDate)) ? (
            <StyledTag color="red">out of statute</StyledTag>
          ) : null}
          {record?.legalStatus === "in_litigation" ? (
            <StyledTag color="orange">in litigation</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment_applied" ? (
            <StyledTag color="green">judgment applied</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment" ? (
            <StyledTag color="green">JUDGMENT APPLIED</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment_applied_archived" ? (
            <StyledTag color="green">JUDGMENT ARCHIVED</StyledTag>
          ) : null}
        </Col>
      ),
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__LAWSUIT_CASE_NUMBER,
      title: "Legal Case #",
      dataIndex: "lawsuitCaseNumber",
      render: (text, record) => record.lawsuitCaseNumber ?? "-",
      visible: true,
      filters: useMemo(() => {
        if (debtorAccounts) {
          return [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.lawsuitCaseNumber)
                .filter((lawsuitCaseNumber) => lawsuitCaseNumber),
            ),
          ].map((lawsuitCaseNumber) => ({
            text: lawsuitCaseNumber,
            value: lawsuitCaseNumber,
          }));
        }
      }, [debtorAccounts]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) =>
        record.lawsuitCaseNumber?.toString().toLowerCase().includes(value.toString().toLowerCase()),
      sorter: (a, b) => a?.lawsuitCaseNumber?.localeCompare(b?.lawsuitCaseNumber),
      sortDirections: ["descend", "ascend"],
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__CURRENT_BALANCE,
      title: "Balance",
      children: [
        {
          title: "Original",
          width: 125,
          dataIndex: "originalTotal",
          render: (text, record) => {
            const balanceItems = [
              {
                key: "originalPrincipal",
                label: "Principal",
                value: formatCurrency(record.originalPrincipal),
              },
              {
                key: "originalInterest",
                label: `Agency Interest (${
                  currency(record?.interestRate, { precision: 4 }).multiply(100).value
                }%)`,
                value: formatCurrency(record.originalInterest),
              },
              {
                key: "originalClientInterest",
                label: "Client Interest",
                value: formatCurrency(record.originalClientInterest),
              },
              ...insertIf(record.legalStatus === "judgment", {
                key: "originalPrejudgmentInterest",
                label: "Prejudgment Interest",
                value: formatCurrency(record.originalPrejudgmentInterest),
              }),
              {
                key: "originalTotalFeesDue",
                label: "Fees",
                value: formatCurrency(record.originalTotalFeesDue),
              },
              {
                key: "originalTotal",
                label: <strong>Total</strong>,
                value: <strong>{formatCurrency(record.originalTotal)}</strong>,
              },
            ];
            const totalBalanceContent = (
              <Col>
                <Descriptions bordered column={1} size="small">
                  {balanceItems.map((item) => (
                    <Descriptions.Item key={item.key} label={item.label}>
                      {item.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Col>
            );
            return (
              <Row align="middle">
                <StyledInfoValue>{formatCurrency(text)}</StyledInfoValue>
                <Popover
                  content={totalBalanceContent}
                  title="Original Balance Breakdown"
                  trigger="hover"
                  placement="right"
                >
                  <StyledQuestionCircleOutlined />
                </Popover>
              </Row>
            );
          },
        },
        {
          title: "Turnover",
          width: 125,
          dataIndex: "turnoverTotalBalance",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Current",
          width: 125,
          dataIndex: "totalBalance",
          render: (text, record) => {
            const balanceItems = [
              {
                key: "principal",
                label: "Principal",
                value: formatCurrency(record.principalBalance),
              },
              {
                key: "interest",
                label: `Agency Interest (${
                  currency(record?.interestRate, { precision: 4 }).multiply(100).value
                }%)`,
                value: formatCurrency(record.interestBalance),
              },
              {
                key: "clientInterest",
                label: "Client Interest",
                value: formatCurrency(record.clientInterestBalance),
              },
              ...insertIf(record.legalStatus === "judgment", {
                key: "prejudgmentInterest",
                label: "Prejudgment Interest",
                value: formatCurrency(record.prejudgmentInterestBalance),
              }),
              {
                key: "totalFees",
                label: "Fees",
                value: formatCurrency(record.totalFeesBalance),
              },
              {
                key: "total",
                label: <strong>Total</strong>,
                value: <strong>{formatCurrency(text)}</strong>,
              },
            ];
            const totalBalanceContent = (
              <Col>
                <Descriptions bordered column={1} size="small">
                  {balanceItems.map((item) => (
                    <Descriptions.Item key={item.key} label={item.label}>
                      {item.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Col>
            );
            return (
              <Row align="middle">
                <StyledInfoValue>{formatCurrency(text)}</StyledInfoValue>
                <Popover
                  content={totalBalanceContent}
                  title="Total Balance Breakdown"
                  trigger="hover"
                  placement="right"
                >
                  <StyledQuestionCircleOutlined />
                </Popover>
              </Row>
            );
          },
          sorter: (a, b) => a.totalBalance - b.totalBalance,
        },
      ],
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__LAST_PAYMENT,
      title: "Last Payment",
      children: [
        {
          title: "Amount",
          dataIndex: "lastPaymentAmount",
          key: "lastPaymentAmount",
          render: (text) => (parseFloat(text) ? formatCurrency(text) : "-"),
          sorter: (a, b) =>
            (parseFloat(a.lastPaymentAmount) || 0) - (parseFloat(b.lastPaymentAmount) || 0),
        },
        {
          title: "Date",
          dataIndex: "lastPaymentDate",
          key: "lastPaymentDate",
          render: (text) => (text ? formatDate(text) : "-"),
          sorter: (a, b) => {
            if (!a.lastPaymentDate && !b.lastPaymentDate) return 0;
            if (!a.lastPaymentDate) return -1;
            if (!b.lastPaymentDate) return 1;
            return new Date(a.lastPaymentDate) - new Date(b.lastPaymentDate);
          },
        },
      ],
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__CLIENT,
      title: "Client",
      width: 175,
      filters: useMemo(() => {
        if (debtorAccounts) {
          return [
            ...new Set(
              debtorAccounts
                .map((debtorAccount) => debtorAccount.creditorId)
                .filter((creditorId) => creditorId),
            ),
          ].map((creditorId) => ({
            text: creditorIdToCreditorMap[creditorId]?.name,
            value: creditorId,
          }));
        }
      }, [debtorAccounts, creditorIdToCreditorMap]),
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) =>
        record.creditorId?.toString().toLowerCase().includes(value.toString().toLowerCase()),
      render: (text, record) => {
        const creditor = creditorIdToCreditorMap[record.creditorId];
        const creditorInfo = (
          <>
            <Row align="middle">
              <StyledInfoTitle>Code:</StyledInfoTitle>
              <StyledInfoValue>
                <a href={`/creditors/${creditor?.id}`} target="_blank" rel="noreferrer">
                  {creditor?.code}
                </a>
              </StyledInfoValue>
            </Row>
            <Row align="middle">
              <StyledInfoTitle>Primary Contact&apos;s Name:</StyledInfoTitle>
              <StyledInfoValue>{creditor?.bestContact?.name || "-"}</StyledInfoValue>
            </Row>
            <Row align="middle">
              <StyledInfoTitle>Primary Contact&apos;s Email:</StyledInfoTitle>
              <StyledInfoValue>{creditor?.bestContact?.email || "-"}</StyledInfoValue>
            </Row>
            <Row align="middle">
              <StyledInfoTitle>Primary Contact&apos;s Phone:</StyledInfoTitle>
              <StyledInfoValue>{creditor?.bestContact?.workPhone || "-"}</StyledInfoValue>
            </Row>
            <Row align="middle">
              <StyledInfoTitle>Address:</StyledInfoTitle>
              <StyledInfoValue>{creditor?.bestAddress?.formatted || "-"}</StyledInfoValue>
            </Row>
          </>
        );
        return (
          <>
            <StyledInfoValue>{creditor?.name}</StyledInfoValue>
            <Popover content={creditorInfo} title={creditor?.name} trigger="click">
              <StyledInfoCircleOutlined />
            </Popover>
          </>
        );
      },
      hideForRolledUpAccounts: true,
      hidden: isCreditorUserType,
      visible: true,
      sorter: {
        compare: (a, b) => {
          const aName = creditorIdToCreditorMap[a.creditorId]?.name;
          const bName = creditorIdToCreditorMap[b.creditorId]?.name;
          if (aName < bName) return -1;
          if (aName > bName) return 1;
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__DESK,
      title: "Desk",
      dataIndex: "deskId",
      render: (text, record) => (
        <Select
          showSearch
          filterOption={filterOption}
          popupMatchSelectWidth={false}
          value={text ?? ""}
          style={{ width: 150 }}
          disabled={!isUpdateDeskAuthorized}
          onChange={async (value) => {
            await onUpdateAccountDesks(record, value === -1 ? null : value);
          }}
          options={[
            { label: "Unassigned", value: -1 },
            ...(desks?.map((desk) => ({ label: desk.name, value: desk.id })) ?? []),
          ]}
          placeholder="Select one..."
        />
      ),
      visible: true,
    },
    {
      key: TABLE_COLUMNS.ACCOUNT_WORKLIST__EXTERNAL_SCORE,
      title: "Score",
      width: 150,
      render: (text, record) => record.externalScore,
      sorter: {
        compare: (a, b) => parseFloat(a.externalScore) - parseFloat(b.externalScore),
      },
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__ORIGINAL_CLIENT,
      title: "Original Creditor",
      width: 175,
      render: (text, record) => record.originalCreditorName,
      hideForRolledUpAccounts: true,
      hidden: isCreditorUserType,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__CURRENT_CLIENT,
      title: "Current Creditor",
      width: 175,
      render: (text, record) => record.currentCreditorName,
      hideForRolledUpAccounts: true,
      hidden: isCreditorUserType,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__FOLLOW_UP_DATE,
      title: "Follow-up Date",
      dataIndex: "followUpDate",
      render: (text, record) => (
        <AktDatePicker
          disabled={isChangeFollowUpDateDisabled(record)}
          value={text}
          onChange={(date) => {
            if (date !== text) {
              onChangeFollowUpDate(record.id, date);
            }
          }}
        />
      ),
      hideForRolledUpAccounts: true,
      visible: true,
      sorter: {
        compare: (a, b) => {
          if (a.followUpDate < b.followUpDate) return -1;
          if (a.followUpDate > b.followUpDate) return 1;
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__STATUS,
      title: "Status",
      dataIndex: "statusName",
      render: (text, record) => (
        <Select
          showSearch
          filterOption={filterOption}
          disabled={isChangeStatusCodeDisabled(record)}
          value={!text ? "" : `${record.statusCode} - ${text}`}
          popupMatchSelectWidth={400}
          style={{ width: 200 }}
          onChange={async (value) => {
            const newStatus = defaultWorkflowStates?.find((state) => state.id === value);
            if (text?.toLowerCase() !== newStatus?.name?.toLowerCase()) {
              await onUpdateAccountStatus(record, newStatus);
            }
          }}
          options={defaultWorkflowStates?.map((state) => ({
            value: state.id,
            label: [state.code, state.name].join(" - "),
          }))}
        />
      ),
      filters: activeStatusFilters,
      filterMode: "menu",
      filterSearch: true,
      onFilter: (value, record) =>
        record.statusCode?.toString().toLowerCase().includes(value.toLowerCase()),
      hideForRolledUpAccounts: true,
      visible: true,
      sorter: {
        compare: (a, b) => {
          if (a.statusName < b.statusName) return -1;
          if (a.statusName > b.statusName) return 1;
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__CO_DEBTOR_IDS,
      title: "Co-Debtors",
      dataIndex: "debtors",
      render: (text, record) => {
        const codebtors = record?.debtors.filter((debtor) => debtor.id.toString() !== debtorId);
        if (codebtors.length === 0) return "-";
        return (
          <Col>
            {codebtors.map((codebtor) => (
              <div key={codebtor.id}>
                <Button type="link" onClick={() => navigate(`/debtors/${codebtor.id}`)}>
                  {codebtor.firstName} {codebtor.middleName} {codebtor.lastName} ({codebtor.id})
                </Button>
              </div>
            ))}
          </Col>
        );
      },
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__TURNOVER_DATE,
      title: "Turnover Date",
      dataIndex: "turnoverDate",
      render: (text, record) => (
        <StyledInfoValue>{formatDate(record?.turnoverDate) ?? "-"}</StyledInfoValue>
      ),
      hideForRolledUpAccounts: true,
      visible: false,
      sorter: {
        compare: (a, b) => {
          if (a.turnoverDate < b.turnoverDate) return -1;
          if (a.turnoverDate > b.turnoverDate) return 1;
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__OPEN_DATE,
      title: "Open Date",
      dataIndex: "openDate",
      render: (text, record) => (
        <StyledInfoValue>{formatDate(record?.openDate) ?? "-"}</StyledInfoValue>
      ),
      hideForRolledUpAccounts: true,
      visible: false,
      sorter: {
        compare: (a, b) => {
          if (a.openDate < b.openDate) return -1;
          if (a.openDate > b.openDate) return 1;
          return 0;
        },
      },
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__OTHER_KEY_DATES,
      title: "Other Key Dates",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Service Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.dateOfService) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Turnover Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.turnoverDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Open Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.openDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Itemization Date:</StyledInfoTitle>
            <StyledInfoValue>{formatDate(record?.itemizationDate) ?? "-"}</StyledInfoValue>
          </Row>
          <Popover
            content={
              <>
                <Row align="middle">
                  <StyledInfoTitle>Service Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.dateOfService) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Turnover Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.turnoverDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Open Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.openDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Upload Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.uploadDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Itemization Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.itemizationDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Date of First Delinquency:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.dateOfFirstDelinquency) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Charge-off Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.chargeOffDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Interest Calculation Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.interestStartDate) ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Out Of Statute Date:</StyledInfoTitle>
                  <StyledInfoValue>
                    {formatDate(record?.statuteOfLimitationsExpiryDate) ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Judgment Date:</StyledInfoTitle>
                  <StyledInfoValue>{formatDate(record?.judgmentDate) ?? "-"}</StyledInfoValue>
                </Row>
              </>
            }
            title="Account Dates"
            trigger="click"
          >
            <StyledButton type="link">See All</StyledButton>
          </Popover>
        </Col>
      ),
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__PAY_READY_FIELDS,
      title: "Pay Ready Fields",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Current Status:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.currentAccountStatuses?.join(", ") ?? "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Status Last Updated:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.accountStatusUpdatedAt
                ? moment(record?.payReadyLease?.accountStatusUpdatedAt).format(DATE_TIME_FORMAT)
                : "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Last Synced:</StyledInfoTitle>
            <StyledInfoValue>
              {record?.payReadyLease?.lastSyncedAt
                ? moment(record?.payReadyLease?.lastSyncedAt).format(DATE_TIME_FORMAT)
                : "-"}
            </StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Move In Date:</StyledInfoTitle>
            <StyledInfoValue>{record?.payReadyLease?.moveInDate ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Move Out Date:</StyledInfoTitle>
            <StyledInfoValue>{record?.payReadyLease?.moveOutDate ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Lease Begin Date:</StyledInfoTitle>
            <StyledInfoValue>{record?.payReadyLease?.leaseBeginDate ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Lease End Date:</StyledInfoTitle>
            <StyledInfoValue>{record?.payReadyLease?.leaseEndDate ?? "-"}</StyledInfoValue>
          </Row>
          <Popover
            content={
              <>
                <Row align="middle">
                  <StyledInfoTitle>Current Status:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.currentAccountStatuses?.join(", ") || "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Status Last Updated:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.accountStatusUpdatedAt
                      ? moment(record?.payReadyLease?.accountStatusUpdatedAt).format(
                          DATE_TIME_FORMAT,
                        )
                      : "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Last Synced:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.lastSyncedAt
                      ? moment(record?.payReadyLease?.lastSyncedAt).format(DATE_TIME_FORMAT)
                      : "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Synced Account Statuses:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.syncedAccountStatuses?.join(", ") ?? "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Placement Status:</StyledInfoTitle>
                  <StyledInfoValue>
                    {camelToWords(snakeToCamelCase(record?.payReadyLease?.placementStatus)) || "-"}
                  </StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Move In Date:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.moveInDate ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Move Out Date:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.moveOutDate ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Lease Begin Date:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.leaseBeginDate ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Lease End Date:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.leaseEndDate ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Property ID:</StyledInfoTitle>
                  <StyledInfoValue>{record?.payReadyLease?.propertyId ?? "-"}</StyledInfoValue>
                </Row>
                <Row align="middle">
                  <StyledInfoTitle>Management Company ID:</StyledInfoTitle>
                  <StyledInfoValue>
                    {record?.payReadyLease?.managementCompanyId ?? "-"}
                  </StyledInfoValue>
                </Row>
              </>
            }
            title="PayReady Fields"
            trigger="click"
          >
            <StyledButton type="link">See All</StyledButton>
          </Popover>
        </Col>
      ),
      visible: agencySettings?.isPayReadyEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__INTELITECH_SCORES,
      title: "Intelitech Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>I-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.iScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Medal-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.medalScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>Q-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.qScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>R-Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.rScore ?? "-"}</StyledInfoValue>
          </Row>
          <Row align="middle">
            <StyledInfoTitle>FPG-Value:</StyledInfoTitle>
            <StyledInfoValue>{record?.intelitechScore?.fpgValue ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isIntelitechEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__LEXIS_NEXIS_SCORES,
      title: "Lexis Nexis Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.lexisNexisScore?.customScore ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isLexisNexisEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__TRANS_UNION_SCORES,
      title: "Trans Union Scores",
      render: (text, record) => (
        <Col>
          <Row align="middle">
            <StyledInfoTitle>DEG Score:</StyledInfoTitle>
            <StyledInfoValue>{record?.transUnionScore?.degScore ?? "-"}</StyledInfoValue>
          </Row>
        </Col>
      ),
      visible: agencySettings?.isTransUnionEnabled,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__COLLECTOR,
      title: "Collector",
      dataIndex: "assigneeId",
      render: (text, record) => (
        <Select
          showSearch
          filterOption={filterOption}
          loading={isCollectorsLoading}
          disabled={isChangeCollectorDisabled(record)}
          popupMatchSelectWidth={false}
          value={text ?? ""}
          style={{ width: 200 }}
          onChange={async (value) => {
            if (value !== text) {
              await onChangeAssignAgent({
                selectedAccountId: record.id,
                agentId: value === -1 ? null : value,
              });
            }
          }}
          options={[
            { label: "Unassigned", value: -1 },
            ...(collectors.map((collector) => ({
              label: collectorFullName(collector),
              value: collector.id,
            })) ?? []),
          ]}
          placeholder="Select one..."
        />
      ),
      hideForRolledUpAccounts: true,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__FORWARDING_ENTITY,
      title: "Forwarding Entity",
      dataIndex: "forwardingEntityId",
      render: (text, record) => (
        <Select
          allowClear
          showSearch
          filterOption={filterOption}
          placeholder="Select one..."
          disabled={isChangeForwardingAgencyDisabled(record)}
          popupMatchSelectWidth={false}
          value={text ?? ""}
          style={{ width: 200 }}
          onChange={async (value) => {
            if (value !== record.forwardingEntityId) {
              await onChangeForwardingEntity({
                selectedAccountId: record.id,
                forwardingEntityId: value ?? null,
              });
            }
          }}
          options={[
            ...(forwardingEntities?.map((forwardingEntity) => ({
              label: forwardingEntity.name,
              value: forwardingEntity.id,
            })) ?? []),
          ]}
        />
      ),
      hideForRolledUpAccounts: true,
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__USER_DEFINED_FIELDS,
      title: "User Defined Fields",
      dataIndex: "customFields",
      render: (_, record) => {
        const customFieldItems = getCustomFieldsItems(record.customFields, accountCustomFields);
        return <UserDefinedFieldValues customFieldItems={customFieldItems} />;
      },
      visible: true,
    },
    {
      key: TABLE_COLUMNS.DEBTOR_ACCOUNTS__ACTIONS,
      title: "Actions",
      fixed: "right",
      align: "center",
      dataIndex: "action",
      render: (_, record) => (
        <Row wrap={false} align="middle">
          {isAgencyUserType && (
            <Space size="middle">
              {isAccountEditAuthorized && (
                <AntDToolTip placement="bottom" title="Edit" key="edit">
                  <EditOutlined key="edit" onClick={() => onUpdateAccountClick(record)} />
                </AntDToolTip>
              )}
              {isAccountDeleteAuthorized && (
                <Popconfirm
                  placement="topLeft"
                  okText="Yes"
                  title="Are you sure you want to delete this account?"
                  onConfirm={() => handleDelete(record)}
                >
                  <DeleteOutlined key="delete-account" />
                </Popconfirm>
              )}
            </Space>
          )}
          {dropdownItems(record).length > 0 && (
            <Dropdown
              menu={{
                // @ts-ignore
                items: dropdownItems(record),
                onClick: (clickHandlerProp) => handleMenuClick(clickHandlerProp, record),
              }}
              trigger={["click"]}
            >
              <Button type="text">
                <Space>
                  <StyledEllipsisOutlined />
                </Space>
              </Button>
            </Dropdown>
          )}
        </Row>
      ),
      hideForRolledUpAccounts: true,
      hidden:
        dropdownItems().length === 0 &&
        (!isAgencyUserType || (!isAccountEditAuthorized && !isAccountDeleteAuthorized)),
      visible: true,
    },
  ].filter((column) => !column.hidden);

  const defaultColumns = isCompactMode ? compactColumns : classicColumns;

  const reconciledColumns = reconcileColumns(defaultColumns, appearance?.items?.[TABLE_ID]).filter(
    (column) => column.visible,
  );

  const ExpandedRow = useCallback(
    (record) => {
      const rolledUpAccounts = debtorAccounts?.filter(
        (debtorAccount) => debtorAccount.legalParentId === record.id,
      );
      return (
        <>
          {isCompactMode && (
            <AccountRowExpansion
              record={record}
              debtorId={debtorId}
              accountCustomFields={accountCustomFields}
              forwardingEntities={forwardingEntities}
              isChangeForwardingAgencyDisabled={isChangeForwardingAgencyDisabled}
              onChangeForwardingEntity={onChangeForwardingEntity}
              filterOption={filterOption}
            />
          )}
          {(isCompactMode ? rolledUpAccounts?.length > 0 : true) && (
            <>
              {isCompactMode ? (
                <StyledRolledUpHeading>Rolled-up Accounts</StyledRolledUpHeading>
              ) : (
                <h4>Rolled-up Accounts</h4>
              )}
              <StyledTable
                loading={isLoading}
                bordered
                scroll={{ x: "max-content" }}
                // @ts-ignore
                columns={reconciledColumns.filter((column) => !column.hideForRolledUpAccounts)}
                dataSource={rolledUpAccounts}
                pagination={false}
              />
            </>
          )}
        </>
      );
    },
    // reconciledColumns intentionally excluded to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      debtorAccounts,
      isLoading,
      debtorId,
      accountCustomFields,
      forwardingEntities,
      isChangeForwardingAgencyDisabled,
      onChangeForwardingEntity,
      isCompactMode,
    ],
  );

  if (isAppearanceLoading) {
    return <Skeleton active />;
  }

  return (
    <>
      {isBulkAssignCollectorModalVisible && (
        <Modal
          maskClosable={false}
          okButtonProps={{ loading: isAssigningCollector, disabled: isAssigningCollector }}
          title="Assign Collector"
          open={isBulkAssignCollectorModalVisible}
          onOk={handleBulkAssignCollector}
          onCancel={closeBulkAssignCollectorModal}
        >
          <Form
            form={bulkAssignCollectorForm}
            validateMessages={{ required: "This is a required field" }}
            layout="vertical"
          >
            <Form.Item
              name="assignToAgentId"
              label="Select an agent to assign"
              rules={[{ required: true }]}
            >
              <Select
                popupMatchSelectWidth={false}
                allowClear
                options={[
                  {
                    label: "Unassigned",
                    value: null,
                  },
                  ...(collectors?.map((collector) => ({
                    label: collectorFullName(collector),
                    value: collector.id,
                  })) ?? []),
                ]}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
      {isBulkUpdateFollowUpDateModalVisible && (
        <Modal
          maskClosable={false}
          title="Update Follow-Up Date"
          open={isBulkUpdateFollowUpDateModalVisible}
          onOk={handleBulkUpdateFollowUpDate}
          onCancel={closeBulkUpdateFollowUpDateModal}
        >
          <Form
            form={bulkUpdateFollowUpDateForm}
            validateMessages={{ required: "This is a required field" }}
            layout="vertical"
          >
            <Form.Item name="followUpDate" label="Follow-up date" rules={[{ required: true }]}>
              <AktDatePicker />
            </Form.Item>
          </Form>
        </Modal>
      )}
      {isBulkUpdateStatusCodeModalVisible && (
        <Modal
          maskClosable={false}
          title="Update Status Code"
          open={isBulkUpdateStatusCodeModalVisible}
          onOk={handleBulkUpdateStatusCode}
          onCancel={closeBulkUpdateStatusCodeModal}
        >
          <Form
            form={bulkUpdateStatusCodeForm}
            validateMessages={{ required: "This is a required field" }}
            layout="vertical"
          >
            <Form.Item name="statusId" label="Status" rules={[{ required: true }]}>
              <Select
                popupMatchSelectWidth={false}
                allowClear
                showSearch
                filterOption={filterOption}
                placeholder="Select status"
                options={defaultWorkflowStates?.map((state) => ({
                  value: state.id,
                  label: [state.code, state.name].join(" - "),
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}

      {isCreditBureauSettingsModalVisible && (
        <CreditBureauSettingsModal
          account={activeRecord}
          title="Credit Bureau Settings"
          open={isCreditBureauSettingsModalVisible}
          onOk={onDoneCreditBureauSettingsModal}
          onCancel={onCreditBureauSettingsModalCancel}
        />
      )}
      {isAgencyUserType && isUpdateAccountModalVisible && (
        <UpdateAccountModal
          account={activeRecord}
          title="Update Account"
          onCancel={onUpdateAccountModalCancel}
          onOk={onDoneUpdateAccountModal}
        />
      )}
      <AccountSequencesModal
        account={activeRecord}
        title={`Manage Communication Sequence (Account ID: ${activeRecord?.id})`}
        open={isSequencesModalVisible}
        onOk={onDoneSequencesModal}
        onCancel={onSequencesModalCancel}
        debtorId={debtorId}
      />
      {isManageCodebtorsModalVisible && (
        <ManageCodebtorsModal
          debtType={debtorPersonalInfo?.type}
          accountId={activeRecord.id}
          title="Manage Codebtors"
          open={isManageCodebtorsModalVisible}
          onCancel={onManageCodebtorsModalCancel}
          debtorId={debtorId}
        />
      )}
      {isDisputeModalVisible && (
        <ManageDisputeFromAccountModal
          dispute={activeRecord?.dispute}
          account={activeRecord}
          title={`Dispute Account (ID: ${activeRecord?.id})`}
          open={isDisputeModalVisible}
          onOk={onManageDisputeModalCancel}
          onCancel={onManageDisputeModalCancel}
          debtorId={debtorId}
        />
      )}
      {isDisputesTableVisible && (
        <AccountDisputesTable
          account={activeRecord}
          debtorId={debtorId}
          open={isDisputesTableVisible}
          onCancel={onDisputesTableCancel}
        />
      )}
      {isCreditReportHistoryModalVisible && (
        <CreditReportHistoryModal
          account={activeRecord}
          title={`Credit Report History (Account ID: ${activeRecord?.id})`}
          open={isCreditReportHistoryModalVisible}
          onCancel={onCreditReportHistoryModalCancel}
          debtorId={debtorId}
        />
      )}
      {isBalanceHistoryModalVisible && (
        <BalanceHistoryModal
          account={activeRecord}
          open={isBalanceHistoryModalVisible}
          onCancel={onBalanceHistoryModalCancel}
          // @ts-ignore
          debtorId={debtorId}
        />
      )}
      {(isChangeCollectorAuthorized ||
        isChangeFollowUpDateAuthorized ||
        isChangeStatusCodeAuthorized ||
        isAppearanceUpdateAuthorized) && (
        <Row wrap={false}>
          <StyledActionContainer>
            <StyledActions wrap={false}>
              <StyledActionItems>
                {isChangeCollectorAuthorized && (
                  <Tooltip title={bulkAssignTooltip("assign collector", canBulkAssignCollector)}>
                    <ButtonWithMargin
                      shape="circle"
                      icon={<UserSwitchOutlined />}
                      type="text"
                      disabled={!canBulkAssignCollector}
                      onClick={() => setBulkAssignCollectorModalVisible(true)}
                    />
                  </Tooltip>
                )}
                {isChangeFollowUpDateAuthorized && (
                  <Tooltip
                    title={bulkAssignTooltip("update follow-up date", canBulkUpdateFollowUpDate)}
                  >
                    <ButtonWithMargin
                      shape="circle"
                      icon={<ScheduleOutlined />}
                      type="text"
                      disabled={!canBulkUpdateFollowUpDate}
                      onClick={() => setBulkUpdateFollowUpDateModalVisible(true)}
                    />
                  </Tooltip>
                )}
                {isChangeStatusCodeAuthorized && (
                  <Tooltip title={bulkAssignTooltip("update status code", canBulkUpdateStatusCode)}>
                    <ButtonWithMargin
                      shape="circle"
                      icon={<AlertOutlined />}
                      type="text"
                      disabled={!canBulkUpdateStatusCode}
                      onClick={() => setBulkUpdateStatusCodeModalVisible(true)}
                    />
                  </Tooltip>
                )}
              </StyledActionItems>
              <StyledActionItems>
                <Switch size="small" checked={showActiveOnly} onChange={setShowActiveOnly} />
                <StyledSwitchLabel>Show active accounts only</StyledSwitchLabel>
                {isAppearanceUpdateAuthorized && (
                  <TriggerModal
                    modal={TableAppearanceModal}
                    tableId={TABLE_ID}
                    defaultColumns={defaultColumns}
                    paginationSelector={false}
                    showActiveOnlySelector
                  >
                    <Tooltip title="Customize Appearance">
                      <ButtonWithMargin shape="circle" icon={<SettingOutlined />} type="text" />
                    </Tooltip>
                  </TriggerModal>
                )}
              </StyledActionItems>
            </StyledActions>
          </StyledActionContainer>
        </Row>
      )}
      {hiddenDesks?.length > 0 && (
        <Alert
          showIcon
          message="Several accounts are hidden "
          type="info"
          description={
            <ul>
              {hiddenDesks?.map((item) => (
                <li key={item.accountExternalId}>
                  Account ID: {item.accountExternalId}, required Desk:{" "}
                  {deskIdToDeskMap[item.deskId]?.name ?? "-"}
                </li>
              ))}
            </ul>
          }
        />
      )}
      <StyledTable
        data-testid="debtor-accounts-table"
        rowKey="id"
        loading={isLoading || isFetching || isAppearanceLoading}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={reconciledColumns}
        rowSelection={rowSelection}
        dataSource={visibleAccounts}
        expandable={
          isCompactMode
            ? {
                expandedRowRender: ExpandedRow,
                rowExpandable: () => true,
                showExpandColumn: true,
              }
            : {
                expandedRowRender: ExpandedRow,
                rowExpandable: (record) =>
                  // @ts-ignore
                  debtorAccounts?.filter(
                    (debtorAccount) => debtorAccount.legalParentId === record?.id,
                  ).length > 0,
                showExpandColumn:
                  debtorAccounts?.filter((debtorAccount) => debtorAccount.legalParentId).length > 0,
              }
        }
        // Hack for Antd table to only sort by ascending and descending and no third state https://github.com/ant-design/ant-design/issues/16747#issuecomment-612047300
        sortDirections={["ascend", "descend", "ascend"]}
      />
    </>
  );
}

export default DebtorAccountsTable;
